const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5000'

function getToken() {
  return localStorage.getItem('sa_token')
}

function setToken(token) {
  localStorage.setItem('sa_token', token)
}

function clearToken() {
  localStorage.removeItem('sa_token')
}

async function request(method, path, body, { timeoutMs = 300_000 } = {}) {
  const headers = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  let res
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Request timed out — check your connection and try again.')
    throw err
  } finally {
    clearTimeout(timer)
  }

  const data = await res.json().catch(() => ({}))

  // Session expired — only redirect if we had a token (not a login failure)
  if (res.status === 401 && getToken()) {
    localStorage.removeItem('sa_token')
    localStorage.removeItem('sa_user')
    window.location.href = '/login'
    return
  }

  if (!res.ok) {
    const err = new Error(data.message || 'Something went wrong.')
    err.status = res.status
    err.code = data.code
    throw err
  }
  return data
}

async function upload(path, formData) {
  const headers = {}
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: formData })
  const data = await res.json().catch(() => ({}))

  if (res.status === 401 && getToken()) {
    localStorage.removeItem('sa_token')
    localStorage.removeItem('sa_user')
    window.location.href = '/login'
    return
  }

  if (!res.ok) throw new Error(data.message || 'Upload failed.')
  return data
}

// Upload a file directly to a pre-signed URL (no auth headers — URL is pre-authorized)
// onProgress(pct: 0-100) is optional
// `contentType` must match exactly what was sent when requesting the signed URL —
// GCS v4 signed URLs embed the content-type in the signature, so a mismatch (e.g.
// trusting the browser's own MIME guess when the URL was signed for a fixed type)
// fails the PUT with a signature error. Defaults to the browser's guess for callers
// that also used that same guess to request the URL.
function putFile(signedUrl, file, onProgress, { timeoutMs = 300_000, contentType } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.timeout = timeoutMs
    if (onProgress) {
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error('Direct upload to storage failed.'))
    }
    xhr.onerror   = () => reject(new Error('Direct upload to storage failed.'))
    xhr.ontimeout = () => reject(new Error('Upload timed out — check your connection and try again.'))
    xhr.open('PUT', signedUrl)
    xhr.setRequestHeader('Content-Type', contentType ?? file.type)
    xhr.send(file)
  })
}

// GET a binary response (e.g. a generated PDF) with the auth header attached,
// then hand the caller an object URL to open/embed — plain <a href> links
// can't carry the Authorization header these endpoints require.
async function downloadFile(path) {
  const headers = {}
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE_URL}${path}`, { headers })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.message || 'Download failed.')
  }
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

export const api = {
  get:     (path, opts)          => request('GET',    path, undefined, opts),
  post:    (path, body, opts)    => request('POST',   path, body, opts),
  patch:   (path, body, opts)    => request('PATCH',  path, body, opts),
  delete:  (path, body, opts)    => request('DELETE', path, body, opts),
  upload:  (path, formData)      => upload(path, formData),
  putFile,
  downloadFile,
}

export { getToken, setToken, clearToken }
